import {AfterContentInit, Component, EventEmitter, OnDestroy, ViewChild} from '@angular/core';
import {combineLatest, Observable, Subscription}                         from 'rxjs';
import {filter, map}                                                     from 'rxjs/operators';
import {
  ApDynComponentComponent
}                                                                        from '../../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColorColumnConfig
}                                                                        from '../../../ap-dyngrids/config/ap-dyn-grid-color-column-config';
import {
  ApDynGridColumnConfigBase
}                                                                        from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfig
}                                                                        from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import {
  ApDynGridPagerConfigBase
}                                                                        from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  ApDynGridPropertyColumnConfig
}                                                                        from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {ApPermissions}                                                   from 'invoker-transport';
import {ApGridPagerType, ApStatsType}                                    from '../../../ap-interface/enums';
import {IGridData, IWizardChoiceData}                                    from '../../../ap-interface/interfaces';
import {
  GetPermissionPipe
}                                                                        from '../../../ap-permission/pipes/get-permission.pipe';
import {ApLodash}                                                        from '../../../ap-utils';
import {
  CropGroupStore
}                                                                        from '../../../stores/base-data/crop.groups.store';
import {
  CropTypeStore
}                                                                        from '../../../stores/base-data/crop.types.store';
import {FarmUsersStore}                                                  from '../../../stores/farm/farm.users.store';
import {FieldStore}                                                      from '../../../stores/farm/field.store';
import {MapViewStore}                                                    from '../../../stores/layout/mapview.store';
import {
  GetMainCropStatisticPipe
}                                                                        from '../../../stores/pipes/ap-get-main-crop-statistic.pipe';
import {
  GetSecondCropStatisticPipe
}                                                                        from '../../../stores/pipes/ap-get-second-crop-statistic.pipe';
import {
  AccessPermissionsStore
}                                                                        from '../../../stores/settings/access.permission.store';
import {
  StatisticStore
}                                                                        from '../../../stores/statistic/statistic.store';
import {
  TranslationStore
}                                                                        from '../../../stores/translation/translation.store';
import {
  CampaignYearStore
}                                                                        from '../../../stores/login/campaignyear.store';
import ICropGroups = Data.BaseData.ICropGroups;

interface ICropGroupsGridData extends IGridData<ICropGroups> {
  Key: string;
  Color: string;
}

/**
 * Component for the cropGroups
 */
@Component({
  selector: 'ap-crop-groups-overview',
  template: `
    <ap-dyncomponent [caption]="'Global__Cropgroups'"
                     [canSearch]="true"
                     [headerIcon]="'ap-icon-settings'"
                     [columns]="columns"
                     [items]="items$ | async"
                     [loading$]="cropGroupsLoadingOrSaving$"
                     [pager]="pager"
                     [selectable]="{checkboxOnly: false, mode: 'multiple'}"
    ></ap-dyncomponent>`,
})

export class ApCropGroupsOverviewComponent implements AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) public dynComponent: ApDynComponentComponent;
  public columns: ApDynGridColumnConfigBase[] = [];
  public items: any[] = [];
  public pager: ApDynGridPagerConfigBase;
  public cropGroupsLoadingOrSaving$: Observable<boolean>;
  public items$ = this.store.Listen((s) => s.data).pipe(
    filter((d) => !!d),
    map((pp) => pp.Convert<ICropGroupsGridData>((cropGroup: ICropGroups) => {
      const key = this.translationStore.FindTranslationForSelectedLanguage(cropGroup.Key);
      let color = cropGroup.Colordefault;
      if (cropGroup.FarmColor) {
        color = cropGroup.FarmColor;
      }
      return {
        Color: color,
        Id: cropGroup.Id,
        Key: key,
        SourceItem: cropGroup,
      };
    })));

  private _listMassEditing = [];
  private _subscriptions: Array<Subscription> = [];
  private _editClick = new EventEmitter<any>();
  private _applyClicked = new EventEmitter<any>();

  constructor(private store: CropGroupStore,
              private translationStore: TranslationStore,
              private farmUsersStore: FarmUsersStore,
              private permissionsStore: AccessPermissionsStore,
              private permissionPipe: GetPermissionPipe,
              private statisticStore: StatisticStore,
              private fieldStore: FieldStore,
              private cropTypeStore: CropTypeStore,
              private mapViewStore: MapViewStore,
              private campaignYearStore: CampaignYearStore,
              private getSecondCropStatisticPipe: GetSecondCropStatisticPipe,
              private getMainCropStatisticPipe: GetMainCropStatisticPipe) {
    this.mapViewStore.showMapView();
    this._subscriptions.push(this._applyClicked.subscribe(() => this._onApplyClicked()));
    this._subscriptions.push(this._editClick.subscribe((item) => this._onColorEdit(item)));
    this._subscriptions.push(this.fieldStore.Listen((s) => s.data).subscribe(() => this.createStatistic()));
    this._subscriptions.push(this.store.Listen((s) => s.data).subscribe(() => this.createStatistic()));

    this.cropGroupsLoadingOrSaving$ = combineLatest([
      this.store.Listen(s => s.loading),
      this.store.Listen(s => s.saving),
      this.cropTypeStore.Listen(s => s.loading),
      this.fieldStore.Listen(s => s.loading)
    ]).pipe(
      map(([loading, saving, croptypesLoading, fieldsLoading]) => loading || saving || croptypesLoading || fieldsLoading)
    );

    this._subscriptions.push(this.store.Listen(s => s.saving).subscribe(saving => {
      // when saving => register for save success (once) to reload croptypes and fields
      if (saving) {
        const savedSubscription = this.store.Listen(s => s.saved).subscribe(saved => {
          if (saved) {
            // Reload croptypes (once) after editing of cropGroups has finished successfully
            this.cropTypeStore.loadCroptypes();
            const cropTypesSubscription = this.cropTypeStore.Listen(_ => _.data).subscribe(cropTypes => {
              if (cropTypes && cropTypes.length > 0) {
                // Reload fields after editing of cropGroups and reload of croptypes has finished successfully
                this.fieldStore.loadFields(this.campaignYearStore.SelectedCampaignYear.DefaultStart);
                cropTypesSubscription.unsubscribe();
                savedSubscription.unsubscribe();
              }
            });
          }
        });
      }
    }));
  }

  public ngAfterContentInit(): void {
    this.fillWizardChoiceData();
    this.gridBuilder();
    this.permissionPipe.transform(ApPermissions.SHOW_STATISTICS).pipe(
      filter((permission) => permission === true),
    ).subscribe(() => this.mapViewStore.goIntoStatisticMenu());
  }

  public ngOnDestroy(): void {
    this.statisticStore.removeStatisticData();
    this._subscriptions.forEach((d) => {
      d.unsubscribe();
    });
  }

  private gridBuilder(): void {
    this.permissionPipe.transform(ApPermissions.EDIT_CROPTYPE_GROUPS).subscribe((permission) => {
      this.columns = [
        new ApDynGridPropertyColumnConfig({
          field: 'Key',
          filterable: true,
          headerFilterable: true,
          sortDesc: false,
          sortIndex: 0,
          title: 'Global__Description',
          width: 300,
        }),
        new ApDynGridColorColumnConfig({
          callback: this._editClick,
          enable: permission,
          field: 'Color',
          key: 'Color',
          filterable: false,
          title: 'Global__Lbl_Standardcolor',
          width: 200,
        }),
      ];
      this.pager = new ApDynGridPagerConfig({
        apply: this._applyClicked,
        hasPermission: permission,
        listMassEditing: this._listMassEditing,
      });
    });
  }

  private createStatistic(): void {
    const mainCropResult = this.getMainCropStatisticPipe.transform();
    const secondCropResult = this.getSecondCropStatisticPipe.transform();
    const mergeResult = [...secondCropResult, ...mainCropResult];
    this.statisticStore.setStatisticData(mergeResult, ApStatsType.MainSecondCrop, this.translationStore.FindTranslationForSelectedLanguage('Global_Distribution_Crop_Groups'));
  }

  private _onColorEdit(item: any): void {
    const cropgroup = item['SourceItem'] as ICropGroups;
    if ((cropgroup.FarmColor == null && item.Color === cropgroup.Colordefault) || cropgroup.FarmColor === item.Color) {
      return;
    }
    if (ApLodash.isValidGridDataSource(cropgroup.FarmCropGroup)) {
      this.store.SetColor(cropgroup.Id, item.Color);
    } else {
      this.store.SetColorAndGroup(cropgroup.Id, item.Color, cropgroup.Id);
    }

    this.store.saveFarmCropGroups(this.store.getCropGroups().Find(_ => _.Id === cropgroup.Id));
  }

  private fillWizardChoiceData(): void {
    const remove = {
      id: 0,
      name: this.translationStore.FindTranslationForSelectedLanguage('Global__Reset'),
      type: ApGridPagerType.None,
      values: null,
    } as IWizardChoiceData;
    this._listMassEditing.push(remove);
  }

  private _onApplyClicked(): void {
    const rows = this.dynComponent.getSelectedItems();
    rows.forEach((row) => {
      const item = row['SourceItem'] as ICropGroups;
      if (item) {
        if (ApLodash.isValidGridDataSource(item.FarmCropGroup)) {
          this.store.deleteFarmCropGroups(item);
        }
      }
    });
  }
}
